import i18n from "@plugins/i18n.ts";
import { isDefined } from "@utils/var.check";
import { reactive } from "vue";

export default {
  state: reactive({
    structureState: {},
    features: {},
    contentPopupState: {
      viewPrerequisites: {},
      editPrerequisites: {},
    },
  }),
  setViewPrerequisitesVisible(id, visible) {
    this.state.contentPopupState.viewPrerequisites[id] = visible;
  },
  setEditPrerequisitesVisible(id, visible) {
    this.state.contentPopupState.editPrerequisites[id] = visible;
  },
  initContentPopupState(sections) {
    sections.forEach((section) => {
      this.state.contentPopupState.editPrerequisites[section.id] = false;
      this.state.contentPopupState.viewPrerequisites[section.id] = false;
    });
  },
  resetContentPopupState() {
    this.state.contentPopupState.editPrerequisites = {};
    this.state.contentPopupState.viewPrerequisites = {};
  },
  initStructureState(lesson, expanded = [], features = {}) {
    if (lesson.sectionData) {
      lesson.sectionData.forEach((section) => {
        this.setStructureStateDefault(section.id);
      });
    }
    expanded.forEach((sectionId) => {
      this.state.structureState[sectionId] = {
        expandLabel: i18n.t("course_plan_hide_activity_label"),
        expanded: true,
        edit: false,
        dirty: false,
        loading: false,
      };
    });
    this.state.features = features;
  },
  resetSectionStructureState(lesson) {
    if (lesson.sectionData) {
      lesson.sectionData.forEach((section) => {
        this.setStructureStateDefault(section.id);
      });
    }
  },
  resetActivityStructureState(lesson, sectionId) {
    if (lesson.sectionData) {
      const section = lesson.sectionData.find((section) => section.id === sectionId);
      if (isDefined(section)) {
        const activities = section.activityData || section.activities;
        if (activities) {
          for (const activity of activities) {
            this.setStructureStateDefault(activity.id);
          }
        }
      }
    }
  },
  setStructureStateDefault(id) {
    this.state.structureState[id] = {
      expandLabel: i18n.t("course_plan_show_activity_label"),
      expanded: false,
      edit: false,
      dirty: false,
      loading: false,
    };
  },
  setExpanded(id, expanded) {
    if (!isDefined(this.state.structureState[id])) {
      this.setStructureStateDefault(id);
    }
    this.state.structureState[id] = {
      ...this.state.structureState[id],
      expanded: expanded,
      expandLabel: expanded
        ? i18n.t("course_plan_hide_activity_label")
        : i18n.t("course_plan_show_activity_label"),
    };
  },
  setEdit(id, edit) {
    if (!isDefined(this.state.structureState[id])) {
      this.setStructureStateDefault(id);
    }
    this.state.structureState[id] = {
      ...this.state.structureState[id],
      edit: edit,
    };
  },
  setCoursePlanDirty(id, dirty) {
    if (!isDefined(this.state.structureState[id])) {
      this.setStructureStateDefault(id);
    }
    this.state.structureState[id] = {
      ...this.state.structureState[id],
      dirty: dirty,
    };
  },
  setLoading(id, loading) {
    if (!isDefined(this.state.structureState[id])) {
      this.setStructureStateDefault(id);
    }
    this.state.structureState[id] = {
      ...this.state.structureState[id],
      loading: loading,
    };
  },
  isEdit(id) {
    if (!isDefined(this.state.structureState[id])) {
      return false;
    }
    return this.state.structureState[id].edit;
  },
  isExpanded(id) {
    if (!isDefined(this.state.structureState[id])) {
      return false;
    }
    return this.state.structureState[id].expanded;
  },
  isDirty(id) {
    if (!isDefined(this.state.structureState[id])) {
      return false;
    }
    return this.state.structureState[id].dirty;
  },
  isLoading(id) {
    if (!isDefined(this.state.structureState[id])) {
      return false;
    }
    return this.state.structureState[id].loading;
  },
  isEditPrerequisiteVisible(id) {
    return !isDefined(this.state.contentPopupState.editPrerequisites[id])
      ? false
      : this.state.contentPopupState.editPrerequisites[id];
  },
  isViewPrerequisiteVisible(id) {
    return !isDefined(this.state.contentPopupState.viewPrerequisites[id])
      ? false
      : this.state.contentPopupState.viewPrerequisites[id];
  },
  sectionToggleLabel(sectionId) {
    return this.state.structureState[sectionId] && this.state.structureState[sectionId].expandLabel
      ? this.state.structureState[sectionId].expandLabel
      : i18n.t("course_plan_show_activity_label");
  },
  isAnyComponentDirty() {
    return Object.values(this.state.structureState).find((state) => {
      return state.dirty === true;
    });
  },
  features() {
    return this.state.features;
  },
  contentPopupState() {
    return this.state.contentPopupState;
  },
};
